import { Component, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { pick } from 'lodash';
import isObject from 'lodash/isObject';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { expandElement } from '../../shared/animation.utils';
import { Breadcrumb } from '../../shared/components/breadcrumb/breadcrumb.component';
import { getContainerType } from '../../shared/container.utils';
import { ageInYears } from '../../shared/date.utils';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { ContainerChange, ChangeLog, FieldChange } from '../../shared/models/change-log.model';
import { Container } from '../../shared/models/container.model';
import { File } from '../../shared/models/file.model';
import { GearRule } from '../../shared/models/gear-rule.model';
import { Origin, OriginType } from '../../shared/models/origin.model';
import { Resolve } from '../../shared/models/resolve.model';
import { TranslationBundle, TranslationService } from '../../translation.service';

export type Auditable = Container | File;

export type QueryType = 'object' | 'field';

export interface DialogData {
  auditable: Auditable
  type: QueryType
  field?: { label: string, key: string }
}

interface AuditTrailDataSourceItem {
  created: string
  method: OriginType
  operation: string
  patchDetails?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
  }
  user: string
  value?: string
}

@Component({
  host: {
    class: 'mat-dialog-component',
    'test-id': 'queries-dialog',
  },
  selector: 'queries-dialog',
  templateUrl: './queries-dialog.component.html',
  styleUrls: ['./queries-dialog.component.scss'],
  animations: [expandElement],
})
export class QueriesDialogComponent implements OnDestroy {
  auditTrailDataSource: AuditTrailDataSourceItem[] = []
  auditTrailExpandedElement = null
  auditTrailVisibleColumns: string[]
  auditable: Auditable
  breadcrumbs: Breadcrumb[] = []
  gearRules: Record<string, GearRule> = {}
  loading = true
  queryType: QueryType
  tabIndex = 0

  private messages = TranslationBundle.EMPTY
  private subscriptions = {
    data: Subscription.EMPTY,
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private dialogRef: MatDialogRef<QueriesDialogComponent>,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private translate: TranslationService,
  ) {
    this.loadData(data);
  }

  get empty(): boolean {
    return this.auditTrailDataSource.length === 0;
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  loadData(data: DialogData): void {
    this.loading = true;
    this.auditable = data.auditable;
    this.queryType = data.type;
    this.setBreadcrumbs(data);
    this.setColumns();
    this.setDataSource(data);
  }

  setBreadcrumbs(data: DialogData): void {
    const parentsByType: { [type in ContainerType]?: string } = Object.entries(data.auditable.parents).reduce(
      (result, [key, value]) => {
        result[key] = value;
        return result;
      }, {}
    );
    // if you want something ordered right, you have to do it yourself
    const parents = [
      parentsByType.group ? parentsByType.group : null,
      parentsByType.project ? `<id:${parentsByType.project}>` : null,
      parentsByType.subject ? `<id:${parentsByType.subject}>` : null,
      parentsByType.session ? `<id:${parentsByType.session}>`: null,
      parentsByType.acquisition ? `<id:${parentsByType.acquisition}>`: null,
      parentsByType.analysis ? `<id:${parentsByType.analysis}>`: null,
    ].filter((value) => value);
    this.coreApi.resolve.resolve({
      path: parents,
    }, { minattr: true }).pipe(
      map((resolver: Resolve) => {
        return ([
          ...resolver.path.filter(pathItem => {
            // filter out the group container if not on the search page
            return pathItem.container_type !== 'group' || pathItem.container_type === 'group' // TODO: fix this && this.router.current.name === 'index.search';
          }).map((pathItem) => {
            return {
              _id: pathItem._id,
              label: pathItem.label,
              containerType: pathItem.container_type,
            };
          }),
          ...[{
            _id: this.getId(data.auditable),
            label: (data.auditable as File)?.name || (data.auditable as Container).label,
            containerType: this.getItemType(data.auditable),
          }],
          ...[this.queryType === 'field' ? {
            _id: data.field.key,
            label: data.field.label,
            containerType: data.field.key,
          } : null].filter(v => v !== null),
        ]).map((breadcrumb, i, breadcrumbs) => {
          // don't add onClick event handler for group or last item
          if (breadcrumb.containerType !== 'group' && i < breadcrumbs.length - 1) {
            breadcrumb['onClick'] = () => this.viewObjectAuditTrail(breadcrumb._id, breadcrumb.containerType);
          }
          return breadcrumb;
        });
      }),
    ).subscribe((breadcrumbs: Breadcrumb[]) => {
      this.breadcrumbs = breadcrumbs.map(breadcrumb => pick(breadcrumb, ['label', 'onClick']) as Breadcrumb);
    });
  }

  setColumns(): void {
    this.auditTrailVisibleColumns = ['timestamp', 'operation', 'method', 'user'];
    this.auditTrailVisibleColumns.splice(1, 0, this.queryType === 'field' ? 'value' : 'icon');
  }

  formatOrigin(origin: Origin): string {
    const rule = this.gearRules[origin.id];
    return origin.type === OriginType.GearRule
      ? this.translate.interpolate(this.messages.get('gearRuleDisplay'), rule.name, rule.last_modified_by.id)
      : origin.id;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatValue(key: string, value: any): string {
    if (value === null) {
      return '';
    }
    if (['age', 'age (years)'].includes(key)) {
      return ageInYears(Number(value)).toString();
    }
    if (isObject(value)) {
      return JSON.stringify(value);
    }
    return value;
  }

  getId(auditable: Auditable): string {
    return (auditable as File)?.file_id || auditable._id;
  }

  getProjectId(auditable: Auditable): string {
    const containerType = this.getItemType(auditable);
    if (containerType === ContainerType.Project) {
      return auditable._id;
    }
    return auditable.parents.project;
  }

  getItemType(auditable: Auditable): string {
    return ((auditable as File)?.file_id) ? 'file' : getContainerType(auditable as Container);
  }

  setDataSource(data: DialogData): void {
    let changeLog$: Observable<ChangeLog>;
    if (data.field) {
      changeLog$ = this.coreApi.changeLog.field(this.getItemType(data.auditable), this.getId(data.auditable), data.field.key);
    } else {
      changeLog$ = this.coreApi.changeLog.get(this.getItemType(data.auditable), this.getId(data.auditable));
    }
    this.subscriptions.data.unsubscribe();
    this.subscriptions.data = combineLatest([
      changeLog$,
      this.coreApi.projects.rules.query(this.getProjectId(data.auditable)),
      this.translate.getBundle('queriesDialog'),
    ]).subscribe({
      next: ([dataSource, gearRules, queriesDialogBundle]) => {
        this.messages = queriesDialogBundle;
        for (const rule of gearRules) {
          this.gearRules[rule._id] = rule;
        }
        if (this.queryType === 'object') {
          this.auditTrailDataSource = ((dataSource.changes ?? []) as ContainerChange[]).map((change: ContainerChange) => {
            const patchDetails = Object.entries(change.patch)
              .filter((v) => v[0] !== 'modified')
              .map(([key, value]) => {
                return {
                  key,
                  value: this.formatValue(key, value),
                };
              });
            return {
              created: change.created,
              method: change.origin.type,
              operation: change.method,
              patchDetails,
              user: this.formatOrigin(change.origin),
            };
          }).filter(el => el.patchDetails.length);
        } else {
          this.auditTrailDataSource = ((dataSource.changes ?? []) as FieldChange[]).map((fieldChange: FieldChange) => {
            return {
              created: fieldChange.created,
              method: fieldChange.origin.type,
              operation: fieldChange.method,
              user: this.formatOrigin(fieldChange.origin),
              value: this.formatValue(data.field.label.toLowerCase(), fieldChange.value),
            };
          });
        }
        this.loading = false;
      },
      error: (error) => {
        this.notifications.error('queriesDialog.query.error');
        this.logger.error(error);
        this.loading = false;
      },
    });
  }

  viewAuditTrail(field: {key: string, value: string}): void {
    // update dialog data
    const updatedData: DialogData = {
      auditable: this.auditable,
      type: 'field',
      field: { label: field.key, key: field.key }, // label - user friendly, key - key for api lookup
    };
    this.loadData(updatedData);
  }

  viewObjectAuditTrail(id: string, containerType: string): void {
    let auditable$: Observable<Auditable>;
    if (containerType === 'file') {
      auditable$ = this.coreApi.files.get(id);
    } else {
      auditable$ = this.coreApi.containers.get<Container>(id);
    }
    // update dialog data
    auditable$.subscribe({
      next: (auditable: Auditable) => {
        const updatedData: DialogData = {
          auditable: auditable,
          type: 'object',
        };
        this.loadData(updatedData);
      },
      error: (error) => {
        this.logger.error(error);
      },
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
